import React, { useCallback, useEffect, useState } from 'react';
import { Carousel } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Award, Gem, ShieldCheck, Truck } from 'react-bootstrap-icons';

import useCarousel from '../Admin/useCarousel';
import useCategories from '../Admin/useCategories';
import HotProducts from '../Content/HotProducts';
import NewArrival from '../Content/NewArrival';
import AlsoLike from '../Content/AlsoLike';
import BestReviews from '../Content/BestReviews';
import { apiUrl, mediaUrl } from '../../config/api';
import useNaturalAspectRatio from '../../hooks/useNaturalAspectRatio';
import './Landingpage.scss';

const PRICE_TIERS = [
  { key: 'five', to: '/product?price_min=0&price_max=15', label: 'Under $15' },
  { key: 'fifteen', to: '/product?price_min=0&price_max=20', label: 'Under $20' },
  { key: 'twenty', to: '/product?price_min=0&price_max=25', label: 'Under $25' },
  { key: 'thirty', to: '/product?price_min=30&price_max=1000', label: '$30 and above' },
];

const GIFT_GUIDES = [
  { key: 'her', to: '/product?guide=her', label: 'For Her' },
  { key: 'mom', to: '/product?guide=mom', label: 'For Mom' },
  { key: 'friend', to: '/product?guide=friend', label: 'For a Friend' },
  { key: 'sister', to: '/product?guide=sister', label: 'For Sister' },
];

const VALUE_PROPS = [
  { Icon: Gem, title: 'Handcrafted', copy: 'Every piece finished by hand by our artisans.' },
  { Icon: Truck, title: 'Ships across the USA', copy: 'Tracked delivery on every order.' },
  { Icon: ShieldCheck, title: 'Secure checkout', copy: 'Protected payments through PayPal.' },
  { Icon: Award, title: 'Quality assured', copy: 'Inspected before it leaves our workshop.' },
];

/**
 * Banner carousel shared by the hero and the mid-page promo strip.
 *
 * The container takes its shape from the first slide's intrinsic
 * dimensions, so a 3:2 hero and a 7.5:1 promo strip each render at
 * their true proportions instead of being cropped to a fixed ratio.
 * Sizing the container (rather than each slide) keeps the carousel
 * height stable if slides differ.
 */
const BannerCarousel = ({ slides, label, priority = false, variant = 'strip' }) => {
  const firstSrc = mediaUrl(slides?.[0]?.image_path);
  // Fall back to the shape each banner type usually has, so the space
  // is reserved correctly before the intrinsic size is known.
  const ratio = useNaturalAspectRatio(firstSrc, variant === 'hero' ? 3 / 2 : 5 / 1);

  if (!slides || slides.length === 0) return null;

  return (
    <Carousel
      className={`banner-carousel banner-carousel--${variant}`}
      style={{ '--banner-ratio': ratio }}
      indicators={slides.length > 1}
      controls={slides.length > 1}
      interval={slides.length > 1 ? 6000 : null}
      pause="hover"
    >
      {slides.map((slide, index) => {
        const src = mediaUrl(slide.image_path);
        if (!src) return null;
        return (
          <Carousel.Item key={slide.id ?? slide.image_path ?? index}>
            <img
              src={src}
              alt={`${label} ${index + 1}`}
              // The hero banner is the largest-contentful-paint element,
              // so it must not be lazy-loaded.
              loading={priority && index === 0 ? 'eager' : 'lazy'}
              fetchpriority={priority && index === 0 ? 'high' : undefined}
              decoding="async"
            />
          </Carousel.Item>
        );
      })}
    </Carousel>
  );
};

/**
 * "Shop by price" tile. The artwork is 3:2 today but the admin can
 * replace it, so the frame follows the file rather than a guess.
 */
const PriceTile = ({ tier, src }) => {
  const ratio = useNaturalAspectRatio(src, 3 / 2);

  return (
    <Link className="banner-tile" to={tier.to} style={{ '--tile-ratio': ratio }}>
      <img src={src} alt={tier.label} loading="lazy" decoding="async" />
      <span className="banner-tile__label">{tier.label}</span>
    </Link>
  );
};

/** Gold / silver editorial row, framed to its own artwork. */
const EditorialRow = ({ to, src, title, alt, copy, cta, reverse = false }) => {
  const ratio = useNaturalAspectRatio(src, 16 / 9);

  return (
    <Link className={`editorial__row${reverse ? ' editorial__row--reverse' : ''}`} to={to}>
      <div className="editorial__media" style={{ '--editorial-ratio': ratio }}>
        <img src={src} alt={alt} loading="lazy" decoding="async" />
      </div>
      <div className="editorial__body">
        <p className="editorial__eyebrow">Collection</p>
        <h3 className="editorial__title">{title}</h3>
        {copy && <p className="editorial__copy">{copy}</p>}
        <span className="editorial__cta">{cta} →</span>
      </div>
    </Link>
  );
};

const Landingpage = () => {
  const { categories, loading: categoriesLoading } = useCategories();
  const { firstCarousel, secondCarousel, loading: carouselLoading } = useCarousel();

  // `landing` used to be read as `landingData.landing.five` while the
  // request was still in flight, which threw on every cold load once
  // the carousel resolved first.
  const [landing, setLanding] = useState(null);
  const [activeTab, setActiveTab] = useState('new');

  const fetchLanding = useCallback(async (signal) => {
    try {
      const response = await fetch(apiUrl('/api/fetch-landing'), { signal });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setLanding(data?.landing || null);
    } catch (error) {
      if (error.name !== 'AbortError') setLanding(null);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchLanding(controller.signal);
    return () => controller.abort();
  }, [fetchLanding]);

  const priceTiers = PRICE_TIERS.filter((tier) => mediaUrl(landing?.[tier.key]));
  const giftGuides = GIFT_GUIDES.filter((guide) => mediaUrl(landing?.[guide.key]));
  const goldImage = mediaUrl(landing?.gold);
  const silverImage = mediaUrl(landing?.silver);

  return (
    <div className="landing">
      {/* ---------- Hero ---------- */}
      <section className="landing-hero" aria-labelledby="hero-heading">
        {carouselLoading ? (
          <div className="skeleton landing-hero__skeleton" aria-hidden="true" />
        ) : (
          <BannerCarousel slides={firstCarousel} label="Featured collection" priority variant="hero" />
        )}

        <div className="landing-hero__intro">
          <p className="landing-hero__eyebrow">Handcrafted Indian jewellery</p>
          <h1 className="landing-hero__title" id="hero-heading">
            Pieces made for the moments you remember
          </h1>
          <p className="landing-hero__subtitle">
            Bridal sets, kundan, polki and temple designs — crafted by hand and shipped
            across the USA.
          </p>
          <div className="landing-hero__actions">
            <Link className="btn btn--primary btn--lg" to="/allproducts">
              Shop the collection
            </Link>
            <Link className="btn btn--outline btn--lg" to="/all-categories">
              Browse categories
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- Value props ---------- */}
      <section className="value-props" aria-labelledby="value-props-heading">
        {/* Named so the h3s below do not skip a heading level. */}
        <h2 className="visually-hidden" id="value-props-heading">
          Why shop with Bansal Attire
        </h2>
        <ul className="value-props__list">
          {VALUE_PROPS.map(({ Icon, title, copy }) => (
            <li key={title} className="value-props__item">
              <span className="value-props__icon">
                <Icon size={20} aria-hidden="true" />
              </span>
              <div>
                <h3 className="value-props__title">{title}</h3>
                <p className="value-props__copy">{copy}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* ---------- Categories ---------- */}
      <section className="section" aria-labelledby="categories-heading">
        <h2 className="section-heading" id="categories-heading">
          Shop by category
        </h2>
        <p className="section-subheading">Find the style that suits the occasion.</p>

        <div className="circle-rail">
          {categoriesLoading
            ? Array.from({ length: 6 }).map((_, index) => (
                <div className="circle-tile" key={index} aria-hidden="true">
                  <span className="skeleton skeleton--circle circle-tile__media" />
                  <span className="skeleton skeleton--text" style={{ width: '70%' }} />
                </div>
              ))
            : categories.map((category) => {
                const src = mediaUrl(category.category_image);
                return (
                  <Link
                    key={category.category_code}
                    className="circle-tile"
                    to={`/category?category=${category.category_code}`}
                  >
                    <span className="circle-tile__media">
                      {src && (
                        <img src={src} alt="" loading="lazy" decoding="async" />
                      )}
                    </span>
                    <span className="circle-tile__label">{category.category_name}</span>
                  </Link>
                );
              })}
        </div>
      </section>

      {/* ---------- New arrivals / hot products ---------- */}
      <section className="section section--alt" aria-labelledby="products-heading">
        <h2 className="visually-hidden" id="products-heading">
          Featured products
        </h2>

        <div className="tab-switch" role="tablist" aria-label="Product collections">
          <button
            type="button"
            role="tab"
            id="tab-new"
            aria-selected={activeTab === 'new'}
            aria-controls="panel-new"
            className={`tab-switch__tab${activeTab === 'new' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('new')}
          >
            New Arrivals
          </button>
          <button
            type="button"
            role="tab"
            id="tab-hot"
            aria-selected={activeTab === 'hot'}
            aria-controls="panel-hot"
            className={`tab-switch__tab${activeTab === 'hot' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('hot')}
          >
            Hot Products
          </button>
        </div>

        {/* Both panels stay mounted so switching tabs does not refetch. */}
        <div id="panel-new" role="tabpanel" aria-labelledby="tab-new" hidden={activeTab !== 'new'}>
          <NewArrival layout="grid" perPage={8} columns={4} />
        </div>
        <div id="panel-hot" role="tabpanel" aria-labelledby="tab-hot" hidden={activeTab !== 'hot'}>
          <HotProducts layout="grid" perPage={8} columns={4} />
        </div>

        <div className="section-cta">
          <Link className="btn btn--outline" to="/allproducts">
            View all products
          </Link>
        </div>
      </section>

      {/* ---------- Shop by price ---------- */}
      {priceTiers.length > 0 && (
        <section className="section" aria-labelledby="price-heading">
          <h2 className="section-heading" id="price-heading">
            Shop by price
          </h2>
          <p className="section-subheading">Something beautiful at every budget.</p>

          <div className="tile-grid tile-grid--two">
            {priceTiers.map((tier) => (
              <PriceTile key={tier.key} tier={tier} src={mediaUrl(landing[tier.key])} />
            ))}
          </div>
        </section>
      )}

      {/* ---------- Secondary banner ---------- */}
      {secondCarousel.length > 0 && (
        <section className="section" aria-label="Featured promotion">
          <BannerCarousel slides={secondCarousel} label="Promotion" variant="strip" />
        </section>
      )}

      {/* ---------- Gift guide ---------- */}
      {giftGuides.length > 0 && (
        <section className="section section--alt" aria-labelledby="guide-heading">
          <h2 className="section-heading" id="guide-heading">
            Shop by gift guide
          </h2>
          <p className="section-subheading">Curated picks for the people who matter.</p>

          <div className="circle-rail">
            {giftGuides.map((guide) => (
              <Link key={guide.key} className="circle-tile circle-tile--lg" to={guide.to}>
                <span className="circle-tile__media">
                  <img src={mediaUrl(landing[guide.key])} alt="" loading="lazy" decoding="async" />
                </span>
                <span className="circle-tile__label">{guide.label}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ---------- Metal editorial ---------- */}
      {(goldImage || silverImage) && (
        <section className="section" aria-labelledby="metal-heading">
          <h2 className="visually-hidden" id="metal-heading">
            Shop by metal
          </h2>

          <div className="editorial page-container">
            {goldImage && (
              <EditorialRow
                to="/product?jewellery_type=gold"
                src={goldImage}
                title="Gold"
                alt="Gold jewellery collection"
                copy={landing?.gold_text}
                cta="Explore gold"
              />
            )}

            {silverImage && (
              <EditorialRow
                to="/product?jewellery_type=silver"
                src={silverImage}
                title="Silver"
                alt="Silver jewellery collection"
                copy={landing?.silver_text}
                cta="Explore silver"
                reverse
              />
            )}
          </div>
        </section>
      )}

      {/* ---------- Recommendations ---------- */}
      <section className="section section--alt" aria-labelledby="recommended-heading">
        <h2 className="section-heading" id="recommended-heading">
          Recommended for you
        </h2>
        <p className="section-subheading">Pieces our customers keep coming back to.</p>

        <AlsoLike layout="grid" columns={4} limit={8} />

        <div className="section-cta">
          <Link className="btn btn--outline" to="/allproducts">
            More products
          </Link>
        </div>
      </section>

      {/* ---------- Reviews ---------- */}
      <section className="section" aria-labelledby="reviews-heading">
        <h2 className="section-heading" id="reviews-heading">
          What our customers say
        </h2>
        <p className="section-subheading">Real photos and words from real orders.</p>

        <BestReviews />

        <div className="section-cta">
          <Link className="btn btn--outline" to="/all-best-reviews">
            Read all reviews
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Landingpage;
